import React from 'react';
import { useTranslation } from 'react-i18next';

import { SelectItem } from 'components/inputs/select/select';

import { FORM_FIELDS, LABELS } from '../../keys';
import { Section, SectionRow } from '../../styles';
import { isFieldDisabledInPartialMode } from '../../utils/partialUpdateHelpers';
import Address from '../address/address';
import { FieldStatusBadge } from '../fieldStatusBadge/FieldStatusBadge';
import FormikInput from '../formikTextInput/formikTextInput';
import BusinessIndustry from './components/businessIndustry/businessIndustry';
import BusinessType from './components/businessType/businessType';
import { CustomTimePicker } from './components/customTimePicker/customTimePicker';
import { Description } from './styles';

interface StepFormProps {
  error?: Record<string, string>;
  countries: SelectItem[];
  isPartialMode?: boolean;
}

export default ({ error, countries, isPartialMode = false }: StepFormProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Description>{t(LABELS.PANEL_DESCRIPTION)}</Description>
      <SectionRow>
        <Section>
          <CustomTimePicker
            fieldPath={FORM_FIELDS.KYC_SCREEN}
            error={error}
            label={t(LABELS.SECTIONS.KYB_SCREENING_DATE.TITLE)}
            disabled={isFieldDisabledInPartialMode(FORM_FIELDS.KYC_SCREEN, isPartialMode)}
          />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.KYC_SCREEN} />
      </SectionRow>
      <SectionRow>
        <Section>
          <CustomTimePicker
            fieldPath={FORM_FIELDS.OFAC_SCREEN}
            error={error}
            label={t(LABELS.SECTIONS.OFAC_SCREENING_DATE.TITLE)}
            disabled={isFieldDisabledInPartialMode(FORM_FIELDS.OFAC_SCREEN, isPartialMode)}
          />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.OFAC_SCREEN} />
      </SectionRow>
      <SectionRow>
        <Section>
          <CustomTimePicker
            fieldPath={FORM_FIELDS.VERIFIED_DATABASE_AT}
            error={error}
            label={t(LABELS.SECTIONS.ID_VERIFIED_AT.TITLE)}
            disabled={isFieldDisabledInPartialMode(FORM_FIELDS.VERIFIED_DATABASE_AT, isPartialMode)}
          />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.VERIFIED_DATABASE_AT} />
      </SectionRow>
      <SectionRow>
        <Section>
          <CustomTimePicker
            fieldPath={FORM_FIELDS.VERIFIED_PROOF_OF_ADDRESS_AT}
            error={error}
            label={t(LABELS.SECTIONS.PROOF_OF_ADDRESS_VERIFIED_AT.TITLE)}
            disabled={isFieldDisabledInPartialMode(FORM_FIELDS.VERIFIED_PROOF_OF_ADDRESS_AT, isPartialMode)}
          />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.VERIFIED_PROOF_OF_ADDRESS_AT} />
      </SectionRow>
      <SectionRow>
        <Section>
          <FormikInput
            label={t(LABELS.SECTIONS.MERCHANT_NAME.TITLE)}
            fieldPath={FORM_FIELDS.NAME}
            placeholder={t(LABELS.SECTIONS.MERCHANT_NAME.PLACEHOLDER)}
            error={error}
            disabled={isFieldDisabledInPartialMode(FORM_FIELDS.NAME, isPartialMode)}
          />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.NAME} />
      </SectionRow>

      <SectionRow>
        <Section>
          <FormikInput
            label={t(LABELS.SECTIONS.MERCHANT_DESCRIPTION.TITLE)}
            fieldPath={FORM_FIELDS.DESCRIPTION}
            placeholder={t(LABELS.SECTIONS.MERCHANT_DESCRIPTION.PLACEHOLDER)}
            error={error}
            disabled={isFieldDisabledInPartialMode(FORM_FIELDS.DESCRIPTION, isPartialMode)}
          />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.DESCRIPTION} />
      </SectionRow>

      <SectionRow>
        <Section>
          <BusinessIndustry fieldPath={FORM_FIELDS.BUSINESS_INDUSTRY} error={error} isPartialMode={isPartialMode} />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.BUSINESS_INDUSTRY} />
      </SectionRow>

      <SectionRow>
        <Section>
          <BusinessType fieldPath={FORM_FIELDS.BUSINESS_TYPE} error={error} isPartialMode={isPartialMode} />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.BUSINESS_TYPE} />
      </SectionRow>

      <SectionRow>
        <Section>
          <FormikInput
            label={t(LABELS.SECTIONS.MERCHANT_EMAIL.TITLE)}
            fieldPath={FORM_FIELDS.EMAIL}
            placeholder={t(LABELS.SECTIONS.MERCHANT_EMAIL.PLACEHOLDER)}
            error={error}
            disabled={isFieldDisabledInPartialMode(FORM_FIELDS.EMAIL, isPartialMode)}
          />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.EMAIL} />
      </SectionRow>

      <SectionRow>
        <Section>
          <FormikInput
            label={t(LABELS.SECTIONS.WEBSITE.TITLE)}
            fieldPath={FORM_FIELDS.WEBSITE}
            placeholder={t(LABELS.SECTIONS.WEBSITE.PLACEHOLDER)}
            error={error}
            disabled={isFieldDisabledInPartialMode(FORM_FIELDS.WEBSITE, isPartialMode)}
          />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.WEBSITE} />
      </SectionRow>

      <SectionRow>
        <Section>
          <Address
            title={LABELS.SECTIONS.ADDRESS.REGISTERED_ADDRESS_TITLE}
            fieldPath={FORM_FIELDS.REGISTERED_ADDRESS}
            countries={countries}
            error={error}
            isPartialMode={isPartialMode}
          />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.REGISTERED_ADDRESS} />
      </SectionRow>

      <SectionRow>
        <Section>
          <Address
            title={LABELS.SECTIONS.ADDRESS.PHYSICAL_ADDRESS_TITLE}
            fieldPath={FORM_FIELDS.PHYSICAL_ADDRESS}
            countries={countries}
            error={error}
            isPartialMode={isPartialMode}
          />
        </Section>
        <FieldStatusBadge fieldName={FORM_FIELDS.PHYSICAL_ADDRESS} />
      </SectionRow>
    </>
  );
};
